import { useState } from 'react';

import { Button } from '../../shared/ui/button';
import { ConfirmDelete } from '../../shared/ui/confirm-delete';
import { CopyField } from '../../shared/ui/copy-field';
import { PlusIcon } from '../../shared/ui/icons';
import { PanelEmpty, PanelFoot, SectionPanel } from '../../shared/ui/section-panel';
import { Table, type IColumn } from '../../shared/ui/table';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import { cn } from '../../shared/lib/cn';
import { relativeTime, shortDate } from '../../shared/lib/format';
import { isRevoked, keyState, scopeWords, type TApiKey } from '../../entities/api-key';
import type { IMintedKey } from './new-key-dialog';
import type { TApiKeys } from './use-api-keys';

const DOT: Record<string, string> = {
  used: 'bg-success',
  unused: 'bg-faint',
  revoked: 'bg-suppressed',
};

interface IApiKeysPanelProps {
  keys: TApiKeys;
  minted: IMintedKey | null;
  onMintedClose: () => void;
  onCreate: () => void;
  timeZone?: string;
}

export const ApiKeysPanel = ({
  keys,
  minted,
  onMintedClose,
  onCreate,
  timeZone,
}: IApiKeysPanelProps) => {
  const [revoking, setRevoking] = useState<TApiKey | null>(null);
  const now = Date.now();
  const rows = keys.keys.data ?? [];
  const live = rows.filter((key) => !isRevoked(key)).length;

  const columns: IColumn<TApiKey>[] = [
    {
      key: 'name',
      header: 'Key',
      width: 'grow',
      ui: true,
      render: (key) => (
        <span className="flex min-w-0 flex-col">
          <span className={cn('truncate', isRevoked(key) ? 'text-faint' : 'text-primary')}>
            {key.name}
          </span>
          <span className="truncate font-mono text-meta text-muted">{key.keyPrefix}</span>
        </span>
      ),
    },
    {
      key: 'access',
      header: 'May do',
      width: 'w-col-verdict',
      ui: true,
      render: (key) => (
        <span className="truncate text-muted" title={scopeWords(key.scopes)}>
          {scopeWords(key.scopes)}
        </span>
      ),
    },
    {
      key: 'used',
      header: 'Last used',
      width: 'w-col-keydate',
      ui: true,
      render: (key) => (
        <span className="flex min-w-0 items-center gap-2">
          <span aria-hidden className={cn('size-dot shrink-0 rounded-full', DOT[keyState(key)])} />
          <span className="truncate text-muted">
            {isRevoked(key)
              ? `revoked ${shortDate(key.revokedAt ?? '', timeZone)}`
              : key.lastUsedAt
                ? relativeTime(key.lastUsedAt, now, timeZone)
                : 'never used'}
          </span>
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: 'w-col-row-actions',
      ui: true,
      render: (key) =>
        !isRevoked(key) && (
          <span className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              disabled={keys.revoke.isPending}
              onClick={() => setRevoking(key)}
            >
              Revoke
            </Button>
          </span>
        ),
    },
  ];

  return (
    <>
      {minted && (
        <SectionPanel title="Your new key" note="shown once · copy it now">
          <div className="flex items-end gap-3 px-card py-4">
            <div className="min-w-0 flex-1">
              <CopyField value={minted.plaintext} label={minted.name} />
            </div>
            <Button variant="secondary" size="sm" onClick={onMintedClose}>
              I’ve copied it
            </Button>
          </div>
          <PanelFoot>
            Once you close this, the key can’t be shown again. If you lose it, revoke it and create
            another.
          </PanelFoot>
        </SectionPanel>
      )}

      <SectionPanel
        title="Keys"
        note={keys.keys.isSuccess ? `${live} active · ${rows.length - live} revoked` : undefined}
      >
        {keys.keys.isError && (
          <div className="p-input">
            <ErrorState error={keys.keys.error} onRetry={() => void keys.keys.refetch()} />
          </div>
        )}
        {keys.keys.isPending && <LoadingState rows={3} className="p-input" />}
        {keys.keys.isSuccess && rows.length === 0 && (
          <PanelEmpty
            title="No keys yet"
            action={
              <Button variant="secondary" size="sm" onClick={onCreate}>
                <PlusIcon size={12} /> New key
              </Button>
            }
          >
            Create one key for each script or service that calls the API, so you can revoke one
            without breaking the others.
          </PanelEmpty>
        )}
        {rows.length > 0 && (
          <Table
            tall
            layoutId="settings-api-keys"
            columns={columns}
            rows={rows}
            rowKey={(key) => key.id}
            muted={isRevoked}
          />
        )}
      </SectionPanel>

      <ConfirmDelete
        open={revoking !== null}
        onOpenChange={(next) => !next && setRevoking(null)}
        entity="key"
        verb="Revoke"
        name={revoking?.name ?? ''}
        note="Whatever uses this key is refused from its next call."
        waiting={keys.revoke.isPending}
        onConfirm={() => {
          if (revoking) keys.revoke.mutate(revoking.id, { onSuccess: () => setRevoking(null) });
        }}
      />
    </>
  );
};
